import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  createCustomGroup,
  deleteCustomGroups,
  readCustomGroup,
  readCustomGroups,
  shareCustomGroupWithClient,
  shareCustomGroupWithUsers,
  updateCustomGroupBreakouts,
  updateCustomGroupName,
} from '@api/custom_groups';

import { customGroupStatus } from '@helpers';

import { Announcement, BookBookmarkIcon } from '@utilities';

import ItemBreakouts from '../../components/ItemBreakouts';
import ListPage from '../../components/ListPage';

import AddBreakout from '../../components/ListPage/components/ActionsToolbar/components/AddBreakout';
import Delete from '../../components/ListPage/components/ActionsToolbar/components/Delete';
import Duplicate from '../../components/ListPage/components/ActionsToolbar/components/Duplicate';
import Rename from '../../components/ListPage/components/ActionsToolbar/components/Rename';
import Share from '../../components/ListPage/components/ActionsToolbar/components/Share';

import { getFilterOptions, getItemColumns } from './utilities/helpers';

const ProductGroups = () => {
  const [groupOverrides, setGroupOverrides] = useState({});
  const [isCreateModalActive, setIsCreateModalActive] = useState(false);
  const filterOptions = useMemo(() => getFilterOptions(), []);
  const itemColumns = useMemo(() => getItemColumns({ groupOverrides }), [groupOverrides]);

  const groups = useSelector((state) => state?.websocket?.customGroups);
  const currentGroupsRef = useRef(groups);
  const previousGroupsRef = useRef(groups);
  const groupRequestsRef = useRef({});

  useEffect(() => {
    return () => {
      Object.values(groupRequestsRef.current).forEach(({ controller }) => controller?.abort());
    };
  }, []);

  useEffect(() => {
    currentGroupsRef.current = groups;

    const previousGroups = previousGroupsRef.current;
    previousGroupsRef.current = groups;

    const changedGroups = groups?.filter((group) => {
      const previousGroup = previousGroups?.find(({ id }) => id === group?.id);

      if (!previousGroup) return true;

      return Number(previousGroup.status) !== Number(group.status);
    });

    changedGroups?.forEach((group) => {
      const status = Number(group.status);
      const previousRequest = groupRequestsRef.current[group.id];
      const generation = (previousRequest?.generation ?? 0) + 1;

      previousRequest?.controller?.abort();
      groupRequestsRef.current[group.id] = { generation };

      setGroupOverrides((currentOverrides) => ({
        ...currentOverrides,
        [group.id]: {
          isNarrativesCompatible: false,
          status,
        },
      }));

      if (status === customGroupStatus.COMPLETED) {
        const controller = new AbortController();
        groupRequestsRef.current[group.id] = { controller, generation };

        const readCompletedGroup = async () => {
          for (let attempt = 0; attempt < 2; attempt += 1) {
            try {
              const { data } = await readCustomGroup({ id: group.id, signal: controller.signal });
              const currentGroup = currentGroupsRef.current?.find(({ id }) => id === group.id);
              const currentRequest = groupRequestsRef.current[group.id];

              if (
                currentRequest?.generation !== generation ||
                Number(currentGroup?.status) !== customGroupStatus.COMPLETED
              ) {
                return;
              }

              setGroupOverrides((currentOverrides) => ({
                ...currentOverrides,
                [group.id]: {
                  isNarrativesCompatible: data?.isNarrativesCompatible,
                  status: customGroupStatus.COMPLETED,
                },
              }));
              groupRequestsRef.current[group.id] = { generation };
              return;
            } catch (error) {
              if (controller.signal.aborted) return;
              if (attempt === 1) console.error(error);
            }
          }
        };

        readCompletedGroup();
      }
    });
  }, [groups]);

  const checkAvailability = (selectedRow) => {
    const group = groups?.find((group) => group?.id === selectedRow?.id);

    if (group) {
      return group?.status >= customGroupStatus.COMPLETED;
    }
  };

  return (
    <>
      <ListPage
        announcementBanner={
          <Announcement
            hasClose
            icon={<BookBookmarkIcon />}
            isBold
            text="Some prompts in Insights require product groups with non-overlapping breakouts because their methodologies depend on mutually exclusive product selections. Where shown, the text “Compatible Custom Groups” indicates this requirement. Use the compatibility tag or filter on this page to find eligible groups. Expand an incompatible group to see which breakouts overlap and, if you own it, edit them as needed."
            variant="info"
          />
        }
        createItem={(params) => createCustomGroup({ ...params, type: 'product' })}
        filterOptions={filterOptions}
        isCreateModalActive={isCreateModalActive}
        itemActions={[
          <AddBreakout
            checkAvailability={checkAvailability}
            groupType="product"
            key="add-item"
            redirect="/dashboard/create-breakout"
          />,
          <Rename
            checkAvailability={checkAvailability}
            key="rename-item"
            updateName={({ name, row }) => updateCustomGroupName({ id: row.id, name })}
          />,
          <Duplicate
            checkAvailability={checkAvailability}
            createDuplicate={(params) => createCustomGroup({ ...params, type: 'product' })}
            key="duplicate-item"
            readItem={(params) => readCustomGroup({ ...params, type: 'product' })}
          />,
          <Share
            key="share-item"
            shareWithClient={shareCustomGroupWithClient}
            shareWithUsers={shareCustomGroupWithUsers}
          />,
          <Delete
            deleteItems={(selectedRows) =>
              deleteCustomGroups({ ids: selectedRows.map(({ id }) => id) })
            }
            key="delete-item"
          />,
        ]}
        itemColumns={itemColumns}
        onCreate={() => setIsCreateModalActive(true)}
        readItems={(params) => readCustomGroups({ ...params, type: 'product' })}
        renderRowExpanded={({ handleSubmit, itemsPage, row }) => (
          <ItemBreakouts
            groupType={{ label: 'Product Group', type: 'product' }}
            item={row?.original}
            itemsPage={itemsPage}
            itemsRow={row?.id}
            onSubmit={handleSubmit}
            readItem={(params) => readCustomGroup({ ...params, type: 'product' })}
            updateItemBreakouts={updateCustomGroupBreakouts}
          />
        )}
        setIsCreateModalActive={setIsCreateModalActive}
        type="Product Group"
      />
    </>
  );
};

export default ProductGroups;
